import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import FacultyLayout from './FacultyLayout';
import { Upload, X, FileText, Image, CheckCircle, Eye } from 'lucide-react';
import mammoth from 'mammoth';
import { lookupAPI, submissionsAPI } from '../../services/api';

interface Category {
  id: number;
  name: string;
  description: string;
  is_active: number;
}

export default function SubmitWork() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    title: '',
    category_id: '',
    description: '',
  });
  const [categories, setCategories] = useState<Category[]>([]);
  const [file, setFile] = useState<File | null>(null);
  const [filePreviewUrl, setFilePreviewUrl] = useState<string | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [docxPreviewHtml, setDocxPreviewHtml] = useState<string>('');
  const [docxPreviewError, setDocxPreviewError] = useState<string>('');
  const [isDragging, setIsDragging] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Create/revoke preview URL when file changes
  useEffect(() => {
    if (file) {
      const url = URL.createObjectURL(file);
      setFilePreviewUrl(url);
      return () => URL.revokeObjectURL(url);
    } else {
      setFilePreviewUrl(null);
    }
  }, [file]);

  useEffect(() => {
    const generateDocxPreview = async () => {
      if (!file) {
        setDocxPreviewHtml('');
        setDocxPreviewError('');
        return;
      }

      const isDocxFile =
        file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
        file.name.toLowerCase().endsWith('.docx');

      if (!isDocxFile) {
        setDocxPreviewHtml('');
        setDocxPreviewError('');
        return;
      }

      try {
        const arrayBuffer = await file.arrayBuffer();
        const result = await mammoth.convertToHtml({ arrayBuffer });
        setDocxPreviewHtml(result.value);
        setDocxPreviewError('');
      } catch (err) {
        console.error('Failed to generate DOCX preview:', err);
        setDocxPreviewHtml('');
        setDocxPreviewError('Preview not available for this DOCX file.');
      }
    };

    generateDocxPreview();
  }, [file]);

  // Fetch categories on mount
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const result = await lookupAPI.getCategories();
        if (!result.error && result.data) {
          setCategories(result.data);
        }
      } catch (err) {
        console.error('Failed to fetch categories:', err);
      }
    };
    fetchCategories();
  }, []);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      setFile(e.dataTransfer.files[0]);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setFile(e.target.files[0]);
    }
  };

  const handleRemoveFile = () => {
    setFile(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const result = await submissionsAPI.create(
        formData.title,
        parseInt(formData.category_id),
        formData.description,
        file || undefined
      );
      
      if (!result.error) {
        setSubmitted(true);
        setTimeout(() => {
          navigate('/faculty/submissions');
        }, 2000);
      } else {
        setError(result.message || 'Failed to submit work');
      }
    } catch (err) {
      setError('An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <FacultyLayout>
        <div className="max-w-2xl mx-auto">
          <div className="bg-white rounded-2xl border border-gray-200 p-12 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              Submission Successful!
            </h2>
            <p className="text-gray-600 mb-6">
              Your creative work has been submitted for review. You'll be notified once the
              administrator reviews your submission.
            </p>
            <div className="inline-flex items-center space-x-2 text-purple-600">
              <div className="w-2 h-2 bg-purple-600 rounded-full animate-pulse" />
              <span>Redirecting to your submissions...</span>
            </div>
          </div>
        </div>
      </FacultyLayout>
    );
  }

  return (
    <FacultyLayout>
      <div className="max-w-3xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Submit Creative Work</h1>
          <p className="text-gray-600">
            Share your creative excellence with the IMDC community
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          {/* Title */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <label htmlFor="title" className="block font-semibold text-gray-900 mb-2">
              Work Title *
            </label>
            <input
              id="title"
              name="title"
              type="text"
              value={formData.title}
              onChange={handleChange}
              required
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              placeholder="Enter the title of your work"
            />
          </div>

          {/* Category */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <label htmlFor="category" className="block font-semibold text-gray-900 mb-2">
              Category *
            </label>
            <select
              id="category"
              name="category_id"
              value={formData.category_id}
              onChange={handleChange}
              required
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent appearance-none bg-white"
            >
              <option value="">Select a category</option>
              {categories.map((cat) => (
                <option key={cat.id} value={cat.id}>
                  {cat.name}
                </option>
              ))}
            </select>
          </div>

          {/* Description */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <label htmlFor="description" className="block font-semibold text-gray-900 mb-2">
              Description *
            </label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              required
              rows={6}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
              placeholder="Describe your work, its inspiration, and any important context..."
            />
            <p className="text-sm text-gray-500 mt-2">
              Provide details to help reviewers understand your creative work
            </p>
          </div>

          {/* File Upload */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <label className="block font-semibold text-gray-900 mb-4">Upload File *</label>

            {!file ? (
              <div
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                className={`border-2 border-dashed rounded-xl p-12 text-center transition-colors ${
                  isDragging
                    ? 'border-purple-500 bg-purple-50'
                    : 'border-gray-300 hover:border-gray-400'
                }`}
              >
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Upload className="w-8 h-8 text-gray-400" />
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">
                  Drag and drop your file here
                </h3>
                <p className="text-gray-600 mb-4">
                  or click to browse from your computer
                </p>
                <input
                  id="file-upload"
                  type="file"
                  onChange={handleFileChange}
                  className="hidden"
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif,.mp4,.mp3"
                />
                <label
                  htmlFor="file-upload"
                  className="inline-block bg-purple-600 text-white px-6 py-3 rounded-lg hover:bg-purple-700 transition-colors cursor-pointer font-medium"
                >
                  Choose File
                </label>
                <p className="text-sm text-gray-500 mt-4">
                  Supported formats: PDF, DOC, DOCX, JPG, PNG, GIF, MP4, MP3 (Max 50MB)
                </p>
              </div>
            ) : (
              <div className="border-2 border-green-200 bg-green-50 rounded-xl p-6">
                <div className="flex items-start justify-between">
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      {file.type.startsWith('image/') ? (
                        <Image className="w-6 h-6 text-green-600" />
                      ) : (
                        <FileText className="w-6 h-6 text-green-600" />
                      )}
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">{file.name}</h4>
                      <p className="text-sm text-gray-600">
                        {(file.size / 1024 / 1024).toFixed(2)} MB
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      type="button"
                      onClick={() => setShowPreview(true)}
                      className="p-2 hover:bg-purple-100 rounded-lg transition-colors"
                      title="View file"
                    >
                      <Eye className="w-5 h-5 text-purple-600" />
                    </button>
                    <button
                      type="button"
                      onClick={handleRemoveFile}
                      className="p-2 hover:bg-red-100 rounded-lg transition-colors"
                    >
                      <X className="w-5 h-5 text-red-600" />
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* File Preview Modal */}
          {showPreview && filePreviewUrl && file && (
            <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setShowPreview(false)}>
              <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between p-4 border-b border-gray-200">
                  <h3 className="font-semibold text-gray-900">{file.name}</h3>
                  <button
                    type="button"
                    onClick={() => setShowPreview(false)}
                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <X className="w-5 h-5 text-gray-600" />
                  </button>
                </div>
                <div className="flex-1 overflow-auto p-4 flex items-center justify-center">
                  {file.type.startsWith('image/') ? (
                    <img
                      src={filePreviewUrl}
                      alt={file.name}
                      className="max-w-full max-h-[70vh] object-contain rounded-lg"
                    />
                  ) : file.type === 'application/pdf' ? (
                    <iframe
                      src={filePreviewUrl}
                      title={file.name}
                      className="w-full h-[70vh] rounded-lg border border-gray-200"
                    />
                  ) :
                    (file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
                      file.name.toLowerCase().endsWith('.docx')) &&
                    !docxPreviewError ? (
                    <div className="w-full h-[70vh] overflow-auto rounded-lg border border-gray-200 p-6 bg-white prose max-w-none">
                      <div dangerouslySetInnerHTML={{ __html: docxPreviewHtml }} />
                    </div>
                  ) : file.type.startsWith('video/') ? (
                    <video controls className="max-w-full max-h-[70vh] rounded-lg">
                      <source src={filePreviewUrl} type={file.type} />
                      Your browser does not support the video tag.
                    </video>
                  ) : file.type.startsWith('audio/') ? (
                    <div className="text-center space-y-4">
                      <div className="w-24 h-24 bg-purple-100 rounded-full flex items-center justify-center mx-auto">
                        <FileText className="w-12 h-12 text-purple-600" />
                      </div>
                      <audio controls className="w-full">
                        <source src={filePreviewUrl} type={file.type} />
                      </audio>
                    </div>
                  ) : (
                    <div className="text-center space-y-4 py-12">
                      <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto">
                        <FileText className="w-12 h-12 text-gray-400" />
                      </div>
                      <p className="text-gray-600">
                        {docxPreviewError || 'Preview not available for this file type.'}
                      </p>
                      <a
                        href={filePreviewUrl}
                        download={file.name}
                        className="inline-flex items-center px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium"
                      >
                        Download to View
                      </a>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Guidelines */}
          <div className="bg-purple-50 border border-purple-200 rounded-xl p-6">
            <h3 className="font-semibold text-purple-900 mb-3">Submission Guidelines</h3>
            <ul className="space-y-2 text-sm text-purple-700">
              <li className="flex items-start">
                <span className="mr-2">•</span>
                <span>Ensure your work is original and created by you</span>
              </li>
              <li className="flex items-start">
                <span className="mr-2">•</span>
                <span>Provide accurate information and detailed descriptions</span>
              </li>
              <li className="flex items-start">
                <span className="mr-2">•</span>
                <span>Files will be reviewed within 3-5 business days</span>
              </li>
              <li className="flex items-start">
                <span className="mr-2">•</span>
                <span>You'll receive feedback and status updates via the dashboard</span>
              </li>
            </ul>
          </div>

          {/* Submit Buttons */}
          <div className="flex items-center justify-end space-x-4">
            <button
              type="button"
              onClick={() => navigate('/faculty/dashboard')}
              className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!file || loading}
              className="px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Submitting...' : 'Submit for Review'}
            </button>
          </div>
        </form>
      </div>
    </FacultyLayout>
  );
}
